/**
 * Preferences Page — Language & Localization Phase 1.
 *
 * Self-contained route at /preferences. Reads & writes the server-side
 * preferences subdoc via /api/preferences/me (GET, PUT). All four fields:
 *   - platformLanguage              (drives in-app i18n)
 *   - contentLanguage               (drives AI generation language)
 *   - autoGenerateInSelectedLanguage(toggle — when off, AI stays in English)
 *   - showOriginalContent           (display-only toggle, no backend effect yet)
 *
 * On platformLanguage change we ALSO call i18n.changeLanguage() so the UI
 * flips instantly even before the PUT completes. Failure to save reverts
 * the i18n change. Server is the source of truth.
 *
 * Disabled gracefully when the backend feature flag is off (404 on GET ->
 * fall back to a "Localization is disabled" notice). Public/library content
 * stays English-only per spec — the "languageNote" copy is shown verbatim.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Languages, Globe, FileText, Eye, ArrowLeft } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { api } from '../services/api';
import { useApp } from '../contexts/AppContext';
import { setPlatformLanguage, SUPPORTED_LANGUAGES } from '../i18n';

const DEFAULT_PREFS = {
  platformLanguage: 'en',
  contentLanguage: 'en',
  autoGenerateInSelectedLanguage: true,
  showOriginalContent: false,
};

export function PreferencesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useApp();
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [supported, setSupported] = useState(SUPPORTED_LANGUAGES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [disabled, setDisabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getMyPreferences();
        if (cancelled) return;
        const next = { ...DEFAULT_PREFS, ...(res.preferences || {}) };
        setPrefs(next);
        if (res.supportedLanguages) {
          // Merge server-supported codes into the UI label map so we always
          // display a localized display label when available.
          const merged = {};
          Object.keys(res.supportedLanguages).forEach((code) => {
            merged[code] = SUPPORTED_LANGUAGES[code] || res.supportedLanguages[code];
          });
          setSupported(merged);
        }
        // Sync the active i18n language with the server preference so the
        // user sees their preferred UI language instantly on this page too.
        setPlatformLanguage(next.platformLanguage, currentUser?.userId);
      } catch (e) {
        if (e?.status === 404) {
          setDisabled(true);
        } else {
          toast.error(t('settings.preferencesError'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [t]);

  const update = async (patch) => {
    const prevPrefs = prefs;
    const optimistic = { ...prefs, ...patch };
    setPrefs(optimistic);
    setSaving(true);
    // If the user is changing the platform language, flip i18n optimistically
    // so the UI starts using the new strings immediately.
    if (patch.platformLanguage) {
      setPlatformLanguage(patch.platformLanguage, currentUser?.userId);
    }
    try {
      const res = await api.updateMyPreferences(patch);
      if (res?.preferences) {
        setPrefs({ ...DEFAULT_PREFS, ...res.preferences });
      }
      toast.success(t('settings.preferencesSaved'));
    } catch (e) {
      // Revert on failure.
      setPrefs(prevPrefs);
      if (patch.platformLanguage) {
        setPlatformLanguage(prevPrefs.platformLanguage, currentUser?.userId);
      }
      toast.error(t('settings.preferencesError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div
        className="flex items-center justify-center min-h-[60vh] text-muted-foreground"
        data-testid="preferences-loading"
      >
        {t('system.loading')}
      </div>
    );
  }

  if (disabled) {
    return (
      <div className="max-w-2xl mx-auto p-6" data-testid="preferences-disabled">
        <Card>
          <CardHeader>
            <CardTitle>{t('settings.languageAndLocalization')}</CardTitle>
            <CardDescription>
              Localization is currently disabled on this deployment.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="max-w-3xl mx-auto p-4 sm:p-6 space-y-6"
      data-testid="preferences-page"
    >
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          const role = currentUser?.role;
          const isIndividual = role === 'teacher' && !currentUser?.schoolId;
          const dest = role === 'super_admin'
            ? '/super-admin/dashboard'
            : role === 'parent'
              ? '/parent-portal'
              : isIndividual
                ? '/teacher-assignments'
                : '/dashboard';
          navigate(dest);
        }}
        className="gap-2 -ml-2 text-muted-foreground hover:text-foreground w-fit"
        data-testid="preferences-back-btn"
      >
        <ArrowLeft className="w-4 h-4" />
        {t('settings.back')}
      </Button>

      <div className="flex items-center gap-3">
        <div className="rounded-lg bg-brand-deep-blue/10 p-2.5">
          <Languages className="w-5 h-5 text-brand-deep-blue" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t('settings.languageAndLocalization')}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {t('settings.languageNote')}
          </p>
        </div>
      </div>

      <Card data-testid="platform-language-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Globe className="w-4 h-4 text-muted-foreground" />
            {t('settings.platformLanguage')}
          </CardTitle>
          <CardDescription>{t('settings.platformLanguageHelp')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-[200px_1fr] items-center gap-3">
            <Label htmlFor="platform-language" className="text-sm font-medium">
              {t('settings.platformLanguage')}
            </Label>
            <Select
              value={prefs.platformLanguage}
              onValueChange={(v) => update({ platformLanguage: v })}
              disabled={saving}
            >
              <SelectTrigger
                id="platform-language"
                data-testid="platform-language-select"
                className="max-w-xs"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(supported).map(([code, label]) => (
                  <SelectItem
                    key={code}
                    value={code}
                    data-testid={`platform-lang-option-${code}`}
                  >
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="content-language-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText className="w-4 h-4 text-muted-foreground" />
            {t('settings.contentLanguage')}
          </CardTitle>
          <CardDescription>{t('settings.contentLanguageHelp')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid sm:grid-cols-[200px_1fr] items-center gap-3">
            <Label htmlFor="content-language" className="text-sm font-medium">
              {t('settings.contentLanguage')}
            </Label>
            <Select
              value={prefs.contentLanguage}
              onValueChange={(v) => update({ contentLanguage: v })}
              disabled={saving}
            >
              <SelectTrigger
                id="content-language"
                data-testid="content-language-select"
                className="max-w-xs"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(supported).map(([code, label]) => (
                  <SelectItem
                    key={code}
                    value={code}
                    data-testid={`content-lang-option-${code}`}
                  >
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Separator />

          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1 max-w-xl">
              <Label
                htmlFor="auto-generate-toggle"
                className="text-sm font-medium cursor-pointer"
              >
                {t('settings.autoGenerateInSelectedLanguage')}
              </Label>
              <p className="text-xs text-muted-foreground">
                {t('settings.autoGenerateInSelectedLanguageHelp')}
              </p>
            </div>
            <Switch
              id="auto-generate-toggle"
              data-testid="auto-generate-toggle"
              checked={prefs.autoGenerateInSelectedLanguage}
              onCheckedChange={(v) => update({ autoGenerateInSelectedLanguage: v })}
              disabled={saving}
            />
          </div>

          <Separator />

          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1 max-w-xl">
              <Label
                htmlFor="show-original-toggle"
                className="text-sm font-medium cursor-pointer flex items-center gap-1.5"
              >
                <Eye className="w-3.5 h-3.5 text-muted-foreground" />
                {t('settings.showOriginalContent')}
              </Label>
              <p className="text-xs text-muted-foreground">
                {t('settings.showOriginalContentHelp')}
              </p>
            </div>
            <Switch
              id="show-original-toggle"
              data-testid="show-original-toggle"
              checked={prefs.showOriginalContent}
              onCheckedChange={(v) => update({ showOriginalContent: v })}
              disabled={saving}
            />
          </div>
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground italic px-1" data-testid="preferences-footer-note">
        {t('settings.languageNote')}
      </p>
    </motion.div>
  );
}

export default PreferencesPage;
